/**
 * Quantas pendências existem — e quais.
 *
 * ═══ POR QUE É UM ESTADO COMPARTILHADO, E NÃO UM `usarBusca` ═══
 *
 * Duas partes da tela precisam do mesmo número ao mesmo tempo: o ponto
 * no menu lateral, que está sempre montado, e a tela de Inteligência,
 * que aparece e some. Se cada uma buscasse por conta, seriam duas
 * chamadas iguais a cada navegação — e a resposta é a mais cara do
 * produto.
 *
 * Aqui há UMA busca, guardada num módulo, e quem precisa se inscreve. A
 * tela de Avisos continua buscando por conta: lá o dado é o conteúdo, e
 * precisa acompanhar o período que a pessoa escolher.
 *
 * ═══ O QUE CONTA COMO PENDÊNCIA ═══
 *
 * Tom 'acao' (exige providência) e 'aviso' (merece ser sabido). O resto
 * é análise e não é pendência de ninguém — foi o erro da primeira
 * versão, que tratava conclusão de negócio e manutenção como a mesma
 * coisa.
 */

import { useEffect, useState } from 'react'
import { api } from '../api/cliente'
import type { Descoberta, PainelDeInteligencia } from '../api/tipos'
import { ehManutencao } from '../descobertas'

/** De quanto em quanto tempo reconferir. */
const INTERVALO_MS = 120_000

let pendencias: Descoberta[] = []
let buscando = false
let ultimaBusca = 0
const inscritos = new Set<() => void>()

function avisarTodos() {
  for (const f of inscritos) f()
}

async function buscar() {
  if (buscando) return
  buscando = true
  try {
    // 24 h fixas, e não o período da tela: o ponto no menu responde
    // "há algo pendente AGORA". Se acompanhasse o filtro, trocar para
    // "últimos 30 dias" faria aparecer pendência já resolvida.
    const d = await api.ler<PainelDeInteligencia>('/api/inteligencia?horas=24')
    pendencias = d.descobertas.filter(
      ehManutencao)
    ultimaBusca = performance.now()
    avisarTodos()
  } catch {
    // Falhar aqui não pode aparecer em lugar nenhum: é um indicador de
    // canto, não conteúdo. Sem resposta, ele simplesmente não aparece.
  } finally {
    buscando = false
  }
}

export function usarPendencias(): Descoberta[] {
  const [, redesenhar] = useState(0)

  useEffect(() => {
    const meu = () => redesenhar(n => n + 1)
    inscritos.add(meu)

    if (!ultimaBusca || performance.now() - ultimaBusca > INTERVALO_MS) {
      void buscar()
    }
    const t = setInterval(() => void buscar(), INTERVALO_MS)
    return () => { inscritos.delete(meu); clearInterval(t) }
  }, [])

  return pendencias
}

/** Força uma reconferência — depois de resolver algo, por exemplo. */
export function reconferirPendencias() {
  ultimaBusca = 0
  void buscar()
}
